// src/components/SPanel/StudentVideoLibrary.jsx
//
// Videoclipurile recomandate, văzute de elev. Folosește exact blocul, cardurile
// și butoanele popup-ului de videoclipuri al adminului (StudentVideoLibraryPopup,
// clasele `studentVideoLibraryPopup__*`), doar că fără adăugare/editare: elevul
// le poate urmări aici (player încorporat) sau pe YouTube.

import React, { useCallback, useEffect, useMemo, useState } from "react";

import { getRecommendedVideos } from "../../api/recommendedVideosService";
import {
   mapRecommendedVideo,
   youtubeEmbedUrl,
   youtubeThumbnailUrl,
} from "../../utils/youtube";

const PAGE_LIMIT = 50;
const MAX_PAGES = 20;

async function fetchAllRecommendedVideos() {
   const out = [];
   for (let page = 1; page <= MAX_PAGES; page += 1) {
      const result = await getRecommendedVideos({ page, limit: PAGE_LIMIT });
      const items = Array.isArray(result?.items) ? result.items : [];
      out.push(...items);

      const total = Number(result?.total);
      const reachedTotal = Number.isFinite(total) && out.length >= total;
      if (reachedTotal || items.length < PAGE_LIMIT) break;
   }
   return out;
}

function openOnYoutube(video) {
   const href = String(video?.watchUrl || "").trim();
   if (!href || typeof window === "undefined") return;
   window.open(href, "_blank", "noopener,noreferrer");
}

export default function StudentVideoLibrary() {
   const [videos, setVideos] = useState([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState("");
   const [searchText, setSearchText] = useState("");
   const [activeVideoId, setActiveVideoId] = useState("");

   const load = useCallback(async () => {
      setLoading(true);
      setError("");
      try {
         const rows = await fetchAllRecommendedVideos();
         setVideos(
            rows
               .map((row, index) => mapRecommendedVideo(row, index))
               .filter(Boolean),
         );
      } catch (e) {
         setVideos([]);
         setError(e?.message || "Nu s-au putut încărca videoclipurile.");
      } finally {
         setLoading(false);
      }
   }, []);

   useEffect(() => {
      load();
   }, [load]);

   const filteredVideos = useMemo(() => {
      const query = String(searchText || "")
         .trim()
         .toLowerCase();
      if (!query) return videos;
      return videos.filter((video) =>
         `${video.title || ""} ${video.youtubeId || ""}`
            .toLowerCase()
            .includes(query),
      );
   }, [searchText, videos]);

   const activeVideo = useMemo(
      () =>
         videos.find((video) => String(video.id) === String(activeVideoId)) ||
         null,
      [activeVideoId, videos],
   );
   const view = activeVideo ? "watch" : "list";

   const handleWatch = (video) => setActiveVideoId(String(video.id));
   const handleBackToList = () => setActiveVideoId("");

   return (
      <section className="student-videos">
         <div className="studentVideoLibraryPopup studentVideoLibraryPopup--page">
            <div className="studentVideoLibraryPopup__header">
               <h3 className="studentVideoLibraryPopup__title">Videoclipuri</h3>
            </div>

            {view === "list" ? (
               <div className="studentVideoLibraryPopup__toolbar">
                  <div className="studentVideoLibraryPopup__toolbarRow">
                     <input
                        type="text"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        placeholder="Caută videoclip"
                        className="studentVideoLibraryPopup__search"
                        autoComplete="off"
                        aria-label="Caută videoclip"
                     />
                  </div>
               </div>
            ) : (
               <div className="studentVideoLibraryPopup__toolbar studentVideoLibraryPopup__toolbar--edit">
                  <button
                     type="button"
                     className="studentVideoLibraryPopup__button"
                     onClick={handleBackToList}
                  >
                     Înapoi
                  </button>
                  <button
                     type="button"
                     className="studentVideoLibraryPopup__button studentVideoLibraryPopup__button--primary"
                     onClick={() => openOnYoutube(activeVideo)}
                  >
                     Deschide pe YouTube
                  </button>
               </div>
            )}

            <div className="studentVideoLibraryPopup__content">
               {view === "watch" && (
                  <>
                     <section className="studentVideoLibraryPopup__preview studentVideoLibraryPopup__preview--player">
                        <div className="studentVideoLibraryPopup__thumb studentVideoLibraryPopup__thumb--large studentVideoLibraryPopup__thumb--player">
                           <iframe
                              key={activeVideo.youtubeId}
                              className="studentVideoLibraryPopup__player"
                              src={youtubeEmbedUrl(activeVideo.youtubeId, {
                                 autoplay: true,
                              })}
                              title={activeVideo.title}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                              allowFullScreen
                              referrerPolicy="strict-origin-when-cross-origin"
                           />
                        </div>
                     </section>

                     <section className="studentVideoLibraryPopup__panel">
                        <div className="studentVideoLibraryPopup__form">
                           <div className="studentVideoLibraryPopup__inputWrap">
                              <span className="studentVideoLibraryPopup__label">
                                 Titlu
                              </span>
                              <p className="studentVideoLibraryPopup__readonly">
                                 {activeVideo.title}
                              </p>
                           </div>
                        </div>
                     </section>
                  </>
               )}

               {view === "list" && (
                  <section className="studentVideoLibraryPopup__list">
                     {loading ? (
                        <div className="studentVideoLibraryPopup__emptyState">
                           <p className="studentVideoLibraryPopup__empty">
                              Se încarcă...
                           </p>
                        </div>
                     ) : error ? (
                        <div className="studentVideoLibraryPopup__emptyState">
                           <p className="studentVideoLibraryPopup__empty">{error}</p>
                           <button
                              type="button"
                              className="studentVideoLibraryPopup__button studentVideoLibraryPopup__button--ghost"
                              onClick={load}
                           >
                              Reîncearcă
                           </button>
                        </div>
                     ) : !filteredVideos.length ? (
                        <div className="studentVideoLibraryPopup__emptyState">
                           <p className="studentVideoLibraryPopup__empty">
                              {videos.length
                                 ? "Nu există videoclipuri pentru filtrul curent."
                                 : "Nu există încă videoclipuri recomandate."}
                           </p>
                        </div>
                     ) : (
                        <div className="studentVideoLibraryPopup__grid">
                           {filteredVideos.map((video) => (
                              <article
                                 key={video.id}
                                 className="studentVideoLibraryPopup__card"
                              >
                                 <div
                                    className="studentVideoLibraryPopup__thumb studentVideoLibraryPopup__thumb--clickable"
                                    style={{
                                       backgroundImage: `url(${youtubeThumbnailUrl(
                                          video.youtubeId,
                                          "mqdefault",
                                       )})`,
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => handleWatch(video)}
                                    onKeyDown={(event) => {
                                       if (event.key !== "Enter" && event.key !== " ")
                                          return;
                                       event.preventDefault();
                                       handleWatch(video);
                                    }}
                                    aria-label={`Urmărește ${video.title}`}
                                 >
                                    <p
                                       className="studentVideoLibraryPopup__cardTitle"
                                       title={video.title}
                                    >
                                       {video.title}
                                    </p>
                                 </div>

                                 <div className="studentVideoLibraryPopup__actions">
                                    <button
                                       type="button"
                                       className="studentVideoLibraryPopup__button studentVideoLibraryPopup__button--ghost"
                                       onClick={() => openOnYoutube(video)}
                                    >
                                       YouTube
                                    </button>
                                    <button
                                       type="button"
                                       className="studentVideoLibraryPopup__button studentVideoLibraryPopup__button--primary"
                                       onClick={() => handleWatch(video)}
                                    >
                                       Vezi
                                    </button>
                                 </div>
                              </article>
                           ))}
                        </div>
                     )}
                  </section>
               )}
            </div>
         </div>
      </section>
   );
}
